import { FC, ReactNode, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Checkbox, Col, Input, Row, Skeleton, Space, Typography } from 'antd';
import { TbHash, TbLock, TbPlugConnected, TbRefresh, TbSearch } from 'react-icons/tb';

import { SlackSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useAlertDeliveryDestinations } from '@/queries';
import { alertsService } from '@/services';
import { EmptySection, SmallTag } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { AlertDeliveryDestination, AlertDeliveryDestinationProvider } from '@/types';

interface DeliveryProviderDefinition {
    connectLabel: string;
    description: string;
    destinationLabel: string;
    emptyDescription: string;
    emptyLabel: string;
    icon: ReactNode;
    label: string;
    unavailableLabel: string;
}

const deliveryProviderDefinitions: Record<AlertDeliveryDestinationProvider, DeliveryProviderDefinition> = {
    slack: {
        connectLabel: 'Connect Slack',
        description: 'Choose one or more channels that should receive matched alert events.',
        destinationLabel: 'Slack Channels',
        emptyDescription: 'Connect a Slack workspace before choosing alert delivery channels.',
        emptyLabel: 'Slack Is Not Connected',
        icon: <img alt='' aria-hidden src={SlackSVGIcon} style={{ height: '1rem', width: '1rem' }} />,
        label: 'Slack',
        unavailableLabel: 'Join in Slack',
    },
};

interface AlertDeliveryDestinationSectionProps {
    onChange: (value?: AlertDeliveryDestination) => void;
    provider: AlertDeliveryDestinationProvider;
    value?: AlertDeliveryDestination;
}

const AlertDeliveryDestinationSection: FC<AlertDeliveryDestinationSectionProps> = ({
    onChange,
    provider,
    value,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const definition = deliveryProviderDefinitions[provider];
    const destinationsQuery = useAlertDeliveryDestinations(provider);
    const [query, setQuery] = useState('');
    const connectedAccountId = destinationsQuery.data?.connectedAccountId;
    const selection = value && value.connectedAccountId === connectedAccountId ? value.destinationIds : [];
    const visibleDestinations = useMemo(() => {
        const destinations = destinationsQuery.data?.destinations ?? [];
        const normalizedQuery = query.trim().toLocaleLowerCase();

        return normalizedQuery
            ? destinations.filter((destination) => destination.label.toLocaleLowerCase().includes(normalizedQuery))
            : destinations;
    }, [destinationsQuery.data?.destinations, query]);

    const connectProvider = async () => {
        try {
            const response = await alertsService.connectDeliveryProvider(provider);
            window.location.href = response.url;
        } catch (error) {
            captureException(error);
            toast.error(`${definition.label} could not be connected.`);
        }
    };

    const toggleDestination = (destinationId: string, checked: boolean) => {
        if (!connectedAccountId) {
            return;
        }

        const destinationIds = checked
            ? Array.from(new Set([...selection, destinationId]))
            : selection.filter((id) => id !== destinationId);

        onChange(destinationIds.length ? { connectedAccountId, destinationIds, provider } : undefined);
    };

    return (
        <section
            id={`alert-delivery-destination-${provider}`}
            tabIndex={-1}
            style={{
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                marginTop: '0.75rem',
                outline: 'none',
                padding: '0.75rem',
            }}
        >
            <Row align='middle' gutter={[8, 0]} wrap={false}>
                <Col flex='none'>
                    <Avatar
                        shape='square'
                        icon={definition.icon}
                        style={{
                            backgroundColor: token.colorFillSecondary,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                        }}
                    />
                </Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        {definition.destinationLabel}
                    </Typography.Title>
                    <small style={{ color: token.colorTextTertiary }}>{definition.description}</small>
                </Col>
                {selection.length ? (
                    <Col flex='none'>
                        <SmallTag message={`${selection.length} selected`} />
                    </Col>
                ) : null}
            </Row>

            {destinationsQuery.isPending ? (
                <div style={{ marginTop: '0.75rem' }}>
                    <Skeleton active paragraph={{ rows: 3 }} title={false} />
                </div>
            ) : destinationsQuery.isError ? (
                <EmptySection
                    description={`${definition.destinationLabel} could not be loaded. Try again without losing the rest of this alert.`}
                    extra={
                        <Button size='small' icon={<TbRefresh />} onClick={() => void destinationsQuery.refetch()}>
                            Try Again
                        </Button>
                    }
                    height='12rem'
                    icon={<TbRefresh />}
                    label='Destinations Unavailable'
                />
            ) : !destinationsQuery.data?.connected ? (
                <EmptySection
                    description={definition.emptyDescription}
                    extra={
                        <Button
                            size='small'
                            type='primary'
                            icon={<TbPlugConnected />}
                            onClick={() => void connectProvider()}
                        >
                            {definition.connectLabel}
                        </Button>
                    }
                    height='12rem'
                    icon={<TbPlugConnected />}
                    label={definition.emptyLabel}
                />
            ) : (
                <Space direction='vertical' size={8} style={{ marginTop: '0.75rem', width: '100%' }}>
                    <Input
                        allowClear
                        size='small'
                        placeholder={`Search ${definition.destinationLabel}...`}
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <div
                        style={{
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: token.borderRadius,
                            maxHeight: '14rem',
                            overflowY: 'auto',
                            padding: '0.25rem',
                        }}
                    >
                        {visibleDestinations.length ? (
                            visibleDestinations.map((destination) => (
                                <Row
                                    key={destination.id}
                                    align='middle'
                                    gutter={[8, 0]}
                                    style={{ minHeight: '2.25rem', padding: '0 0.5rem' }}
                                    wrap={false}
                                >
                                    <Col flex='none'>
                                        <Checkbox
                                            checked={selection.includes(destination.id)}
                                            disabled={!destination.selectable}
                                            onChange={(event) =>
                                                toggleDestination(destination.id, event.target.checked)
                                            }
                                        />
                                    </Col>
                                    <Col flex='none' style={{ color: token.colorTextTertiary }}>
                                        {destination.visibility === 'private' ? <TbLock /> : <TbHash />}
                                    </Col>
                                    <Col flex='auto' style={{ minWidth: 0 }}>
                                        <span style={{ color: token.colorTextSecondary }}>{destination.label}</span>
                                    </Col>
                                    {!destination.selectable ? (
                                        <Col flex='none'>
                                            <SmallTag message={definition.unavailableLabel} />
                                        </Col>
                                    ) : null}
                                </Row>
                            ))
                        ) : (
                            <EmptySection
                                description='Try another destination name or update the connected workspace.'
                                height='9rem'
                                icon={<TbHash />}
                                label='No Destinations Found'
                            />
                        )}
                    </div>
                </Space>
            )}
        </section>
    );
};

export default AlertDeliveryDestinationSection;
